import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  SupportedLanguage, 
  DEFAULT_LANGUAGE, 
  isValidLanguage,
  getLanguageFromStorage,
  setLanguageToStorage,
  getLanguageFromBrowser,
  interpolateParams
} from '../i18n';

export interface LanguageContextType {
  currentLanguage: SupportedLanguage;
  translations: Record<string, string>;
  isLoading: boolean;
  error: string | null;
  changeLanguage: (lang: SupportedLanguage) => Promise<void>;
  t: (key: string, params?: Record<string, any>) => string;
  getAvailableLanguages: () => SupportedLanguage[];
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState<SupportedLanguage>(DEFAULT_LANGUAGE);
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Инициализация языка при загрузке
  useEffect(() => {
    initializeLanguage();
  }, []);

  const initializeLanguage = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Приоритет: localStorage > браузер > дефолт
      const storedLanguage = getLanguageFromStorage();
      const browserLanguage = getLanguageFromBrowser();
      const initialLanguage = storedLanguage || browserLanguage;

      await loadTranslations(initialLanguage);
      setCurrentLanguage(initialLanguage);
      
    } catch (err) {
      console.error('Failed to initialize language:', err);
      setError('Failed to load translations');
      setCurrentLanguage(DEFAULT_LANGUAGE);
    } finally {
      setIsLoading(false);
    }
  };

  const loadTranslations = async (language: SupportedLanguage): Promise<void> => {
    try {
      const response = await fetch(`/api/translations/${language}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch translations: ${response.status}`);
      }

      const data = await response.json();
      setTranslations(data.translations || {});
      
    } catch (err) {
      console.error(`Failed to load translations for ${language}:`, err);
      
      // Fallback на английский если текущий язык не английский
      if (language !== DEFAULT_LANGUAGE) {
        console.log('Attempting fallback to English...');
        try {
          const fallbackResponse = await fetch(`/api/translations/${DEFAULT_LANGUAGE}`);
          if (fallbackResponse.ok) {
            const fallbackData = await fallbackResponse.json();
            setTranslations(fallbackData.translations || {});
            return;
          }
        } catch (fallbackErr) {
          console.error('Fallback to English also failed:', fallbackErr);
        }
      }
      
      // Если все failed, устанавливаем пустой объект
      setTranslations({});
      throw err;
    }
  };

  const changeLanguage = async (newLanguage: SupportedLanguage): Promise<void> => {
    if (newLanguage === currentLanguage) return;

    try {
      setIsLoading(true);
      setError(null);

      // Загружаем новые переводы
      await loadTranslations(newLanguage);
      
      // Обновляем состояние
      setCurrentLanguage(newLanguage);
      setLanguageToStorage(newLanguage);

      // Если пользователь авторизован, сохраняем выбор на сервере
      await saveLanguageToServer(newLanguage);

    } catch (err) {
      console.error('Failed to change language:', err);
      setError('Failed to change language');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const saveLanguageToServer = async (language: SupportedLanguage): Promise<void> => {
    try {
      const token = localStorage.getItem('vst_shop_auth_token');
      if (!token) return; // Пользователь не авторизован

      const response = await fetch(`/api/translations/user/language`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ language }),
      });

      if (!response.ok) {
        console.warn('Failed to save language preference to server:', response.status);
      }
    } catch (err) {
      console.warn('Failed to save language preference to server:', err);
      // Не бросаем ошибку, так как это не критично
    }
  };

  const t = (key: string, params?: Record<string, any>): string => {
    const translation = translations[key];
    
    if (translation) {
      return interpolateParams(translation, params);
    }

    // Если перевод не найден, возвращаем ключ
    console.warn(`Translation not found for key: ${key}`);
    return key;
  };

  const getAvailableLanguages = (): SupportedLanguage[] => {
    return ['en', 'ru'];
  };

  const contextValue: LanguageContextType = {
    currentLanguage,
    translations,
    isLoading,
    error,
    changeLanguage,
    t,
    getAvailableLanguages
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguageContext = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguageContext must be used within a LanguageProvider');
  }
  return context;
}; 